import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { AIAssistant } from './components/AIAssistant';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Dashboard } from './pages/Dashboard';
import { Projects } from './pages/Projects';
import { ProjectForm } from './pages/ProjectForm';
import { ProjectDetail } from './pages/ProjectDetail';
import { Reports } from './pages/Reports';
import { Users } from './pages/Users';
import { Profile } from './pages/Profile';
import Godziny from './pages/Godziny';
import WorkerPortal from './pages/WorkerPortal';
import { KPIDashboard } from './components/KPIDashboard';
import { KanbanBoard } from './pages/KanbanBoard';
import { Settings } from './pages/Settings';
import { ArchivePage } from './pages/Archive';
import { Clients } from './pages/Clients';
import { ClientDetail } from './pages/ClientDetail';
import { Help } from './pages/Help';
import { FieldLeadForm } from './pages/FieldLeadForm';
import { Leads } from './pages/Leads';
import { LeadInbox } from './pages/LeadInbox';
import { NotificationsPage } from './pages/NotificationsPage';
import OfferConfigurator from './pages/OfferConfigurator';
import { NotFoundPage, ForbiddenPage, ServerErrorPage } from './components/ErrorPages';
import { SessionManager } from './components/SessionManager';
import { OfflineBanner } from './components/OfflineBanner';
import '@/App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--crm-bg-main)] flex items-center justify-center">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Lead feeder can ONLY access /lead-terenowy
  if (user.role === 'lead_feeder' && location.pathname !== '/lead-terenowy') {
    return <Navigate to="/lead-terenowy" replace />;
  }

  if (location.pathname.startsWith('/godziny') && user.available_features && !user.available_features.includes('kadry')) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--crm-bg-main)] flex items-center justify-center">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const SessionManagerWrapper = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user || location.pathname.startsWith('/czas/')) return null;
  return <SessionManager />;
};

// Route that redirects montaz users to their tasks page
const ProjectsRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--crm-bg-main)] flex items-center justify-center">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Montaz users are redirected to their tasks page
  if (user.role === 'montaz') {
    return <Navigate to="/profile" replace />;
  }

  // Lead feeder can ONLY access /lead-terenowy
  if (user.role === 'lead_feeder') {
    return <Navigate to="/lead-terenowy" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProjectsRoute>
                <Projects />
              </ProjectsRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedRoute>
                <ProjectForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lead-terenowy"
            element={
              <ProtectedRoute>
                <FieldLeadForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <ProtectedRoute>
                <Leads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/skrzynka-leadow"
            element={
              <ProtectedRoute>
                <LeadInbox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/powiadomienia"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <ProjectDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kanban"
            element={
              <ProtectedRoute>
                <KanbanBoard />
              </ProtectedRoute>
            }
          />
          <Route path="/montaz" element={<Navigate to="/projects" replace />} />
          <Route path="/montaze" element={<Navigate to="/projects" replace />} />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <AdminRoute>
                <Users />
              </AdminRoute>
            }
          />
          <Route path="/kadry" element={<Navigate to="/godziny" replace />} />
          <Route path="/godziny" element={<ProtectedRoute><Godziny /></ProtectedRoute>} />
          <Route path="/czas/:token" element={<WorkerPortal />} />
          <Route
            path="/kpi"
            element={
              <ProtectedRoute>
                <KPIDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <AdminRoute>
                <Settings />
              </AdminRoute>
            }
          />
          <Route
            path="/archiwum"
            element={
              <ProtectedRoute>
                <ArchivePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/klienci"
            element={
              <ProtectedRoute>
                <Clients />
              </ProtectedRoute>
            }
          />
          <Route
            path="/klienci/:id"
            element={
              <ProtectedRoute>
                <ClientDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/konfigurator"
            element={
              <ProtectedRoute>
                <OfferConfigurator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pomoc"
            element={
              <ProtectedRoute>
                <Help />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/500" element={<ServerErrorPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <SessionManagerWrapper />
        <AIAssistant />
      </BrowserRouter>
      <OfflineBanner />
      <Toaster position="top-right" />
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;